import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCountryName } from './countryUtils';
import CountryFlag from 'react-country-flag';
import { getQuarterlyIssues } from './quarterlyIssues';
import './CountryProfile.css';

// Helper function to convert issue number to quarter date
function getIssueDate(issueNum) {
    // Issue 144 = 10/01/2025
    const latestIssue = 144;
    const latestDate = new Date(2025, 9, 1); // October 1, 2025
    
    const issuesDiff = latestIssue - issueNum;
    
    const date = new Date(latestDate);
    date.setMonth(date.getMonth() - issuesDiff * 3);
    
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
}

// Helper function to calculate the date for a given issue number
function calculateIssueDate(issueNum) {
    const baselineIssue = 144;
    const baselineDate = new Date(2025, 9, 1); // Oct 1, 2025
    const issuesDiff = issueNum - baselineIssue;
    const date = new Date(baselineDate);
    date.setMonth(date.getMonth() + issuesDiff * 3);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
}

function CountryProfile({ darkMode }) {
    const { countryCode } = useParams();
    const navigate = useNavigate();
    const [fighters, setFighters] = useState([]);
    const [currentFighters, setCurrentFighters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [gender, setGender] = useState('men');
    const [sortConfig, setSortConfig] = useState({ key: 'global_rank', direction: 'asc' });
    const [currentSortConfig, setCurrentSortConfig] = useState({ key: 'global_rank', direction: 'asc' });
    const [latestIssue, setLatestIssue] = useState(null);
    const [latestIssueDate, setLatestIssueDate] = useState(null);

    // Get country display name
    const getCountryDisplayName = (code) => {
        return getCountryName(code);
    };

    // Fetch fighters for this country (all issues, highest ranked)
    useEffect(() => {
        fetchFighters();
        fetchCurrentFighters();
    }, [gender]);

    const fetchFighters = async () => {
        setLoading(true);
        setError('');
        try {
            const apiBase = process.env.REACT_APP_API_URL || '';
            const res = await fetch(
                `${apiBase}/api/country-fighters-all-time?country_code=${countryCode}&gender=${gender}`
            );
            const data = await res.json();
            if (res.ok && Array.isArray(data)) {
                setFighters(data);
                // Determine latest issue from the data
                if (data.length > 0) {
                    const issues = data.map(f => f.issue).filter(issue => issue);
                    if (issues.length > 0) {
                        const maxIssue = Math.max(...issues);
                        setLatestIssue(maxIssue);
                        setLatestIssueDate(calculateIssueDate(maxIssue));
                    }
                }
            } else {
                setError(data.error || 'Failed to fetch fighters');
            }
        } catch (e) {
            setError('Server error');
        }
        setLoading(false);
    };

    const fetchCurrentFighters = async () => {
        try {
            const apiBase = process.env.REACT_APP_API_URL || '';
            const res = await fetch(
                `${apiBase}/api/country-current-fighters?country_code=${countryCode}&gender=${gender}`
            );
            const data = await res.json();
            if (res.ok && Array.isArray(data)) {
                setCurrentFighters(data);
            }
        } catch (e) {
            // Silently fail if current fighters endpoint not available
        }
    };

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const handleCurrentSort = (key) => {
        setCurrentSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const getSortIcon = (key) => {
        const activeColor = '#888';
        const inactiveColor = '#bdbdbd';
        const isActive = sortConfig.key === key;
        const direction = isActive ? sortConfig.direction : null;
        
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, fontSize: '0.75em', color: direction === 'asc' ? activeColor : direction === 'desc' ? activeColor : inactiveColor }}>
                {direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : '↑↓'}
            </span>
        );
    };

    const getSortIconCurrent = (key) => {
        const activeColor = '#888';
        const inactiveColor = '#bdbdbd';
        const isActive = currentSortConfig.key === key;
        const direction = isActive ? currentSortConfig.direction : null;
        
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, fontSize: '0.75em', color: direction === 'asc' ? activeColor : direction === 'desc' ? activeColor : inactiveColor }}>
                {direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : '↑↓'}
            </span>
        );
    };

    const getSortedFighters = () => {
        let filtered = fighters.filter(f => f.global_rank < 1500);
        if (!sortConfig.key) return filtered;
        
        const sorted = [...filtered].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];
            
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        
        return sorted;
    };

    const getSortedCurrentFighters = () => {
        let filtered = currentFighters.filter(f => f.global_rank < 1500);
        if (!currentSortConfig.key) return filtered;
        
        const sorted = [...filtered].sort((a, b) => {
            const aVal = a[currentSortConfig.key];
            const bVal = b[currentSortConfig.key];
            
            if (aVal < bVal) return currentSortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return currentSortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        
        return sorted;
    };

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2em' }}>
            {/* Back button */}
            <button
                onClick={() => navigate('/country-rankings')}
                style={{
                    background: 'none',
                    border: 'none',
                    color: '#888',
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    marginBottom: '1.5em',
                    padding: '0.5em 0',
                    transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#ddd'}
                onMouseLeave={(e) => e.target.style.color = '#888'}
            >
                ← Back to Country Rankings
            </button>

            {/* Country header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5em', marginBottom: '2em' }}>
                {countryCode && countryCode !== 'RU-WHOLE' && countryCode !== 'EU-NORU' ? (
                    <CountryFlag
                        countryCode={countryCode === 'UK' ? 'GB' : countryCode}
                        svg
                        style={{ width: '4em', height: '4em' }}
                    />
                ) : (
                    <div style={{ width: '4em', height: '4em', background: '#444', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: '2em' }}>
                        🌍
                    </div>
                )}
                <div>
                    <h1 style={{ margin: '0 0 0.5em 0', fontSize: '2.5em', fontWeight: 500 }}>
                        {getCountryDisplayName(countryCode)}
                    </h1>
                </div>
            </div>

            {/* Gender Toggle */}
            <div style={{ marginBottom: '2em', display: 'flex', alignItems: 'center', gap: '1em' }}>
                <span style={{ color: '#ddd', fontWeight: 500 }}>Gender:</span>
                <div 
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.35rem 0.5rem',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'all 0.25s ease'
                    }}
                    onClick={() => setGender(gender === 'men' ? 'women' : 'men')}
                >
                    <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '40px', textAlign: 'center' }}>Men</span>
                    <div style={{
                        width: '32px',
                        height: '20px',
                        background: '#444',
                        borderRadius: '10px',
                        position: 'relative',
                        transition: 'all 0.25s ease',
                        border: '1px solid #555'
                    }}>
                        <div style={{
                            width: '16px',
                            height: '16px',
                            background: '#fff',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: gender === 'men' ? '2px' : '14px',
                            transition: 'all 0.25s ease'
                        }} />
                    </div>
                    <span style={{ fontSize: '0.9rem', color: '#ddd', fontWeight: 500, minWidth: '40px', textAlign: 'center' }}>Women</span>
                </div>
            </div>

            {/* Fighters table */}
            {error && <div style={{ color: '#ff6b6b', marginBottom: '1em', fontSize: '1.1em' }}>{error}</div>}

            {loading ? (
                <div style={{ color: '#aaa', textAlign: 'center', padding: '2em' }}>Loading...</div>
            ) : (
                <>
                    {/* Current Ranks Table */}
                    {currentFighters.length > 0 && (
                        <div style={{ marginBottom: '3em' }}>
                            <h2 style={{ fontSize: '1.5em', fontWeight: 500, marginBottom: '1em', color: '#ddd' }}>
                                Current Ranks {latestIssueDate && `(${latestIssueDate})`}
                            </h2>
                            <div style={{ border: '1px solid #555', borderRadius: '4px', overflow: 'hidden', maxHeight: '500px', overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: darkMode ? '#222' : '#f5f5f5', color: darkMode ? '#f1f1f1' : '#222' }}>
                                            <th style={{ padding: '0.3em', textAlign: 'center', fontWeight: 600, borderBottom: '1px solid #555' }}>#</th>
                                            <th style={{ padding: '0.3em', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #555' }}>Fighter Name</th>
                                            <th style={{ padding: '0.3em', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #555' }}>Division</th>
                                            <th style={{ padding: '0.3em', textAlign: 'center', fontWeight: 600, borderBottom: '1px solid #555' }}>
                                                <button
                                                    onClick={() => handleCurrentSort('global_rank')}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: darkMode ? '#f1f1f1' : '#222',
                                                        fontWeight: 600,
                                                        cursor: 'pointer',
                                                        padding: 0,
                                                        fontSize: '1em',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                    }}
                                                    aria-label="Sort by global rank"
                                                >
                                                    Global Rank
                                                    {getSortIconCurrent('global_rank')}
                                                </button>
                                            </th>
                                            <th style={{ padding: '0.3em', textAlign: 'center', fontWeight: 600, borderBottom: '1px solid #555' }}>
                                                <button
                                                    onClick={() => handleCurrentSort('division_rank')}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: darkMode ? '#f1f1f1' : '#222',
                                                        fontWeight: 600,
                                                        cursor: 'pointer',
                                                        padding: 0,
                                                        fontSize: '1em',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                    }}
                                                    aria-label="Sort by division rank"
                                                >
                                                    Division Rank
                                                    {getSortIconCurrent('division_rank')}
                                                </button>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getSortedCurrentFighters().map((fighter, idx) => {
                                            const sortedCurrentFighters = getSortedCurrentFighters();
                                            const isTie = idx > 0 && fighter.global_rank === sortedCurrentFighters[idx - 1].global_rank;
                                            return (
                                            <tr
                                                key={fighter.name + fighter.division + idx}
                                                style={{
                                                    borderBottom: '1px solid #444',
                                                    background: idx % 2 === 0 ? (darkMode ? '#1a1a1a' : '#f9f9f9') : (darkMode ? '#1a1a1a' : '#f5f5f5'),
                                                    transition: 'background 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background = darkMode ? '#252525' : '#efefef';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = idx % 2 === 0 ? (darkMode ? '#1a1a1a' : '#f9f9f9') : (darkMode ? '#1a1a1a' : '#f5f5f5');
                                                }}
                                            >
                                                <td style={{ padding: '0.3em', textAlign: 'center', color: '#ddd' }}>{isTie ? ' ' : idx + 1}</td>
                                                <td style={{ padding: '0.3em', color: '#ddd' }}>
                                                    {fighter.profile_link ? (
                                                        <a
                                                            href={`https://www.fightmatrix.com${fighter.profile_link}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: '#6ba3ff',
                                                                textDecoration: 'none',
                                                                transition: 'color 0.2s ease'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.color = '#8bb5ff'}
                                                            onMouseLeave={(e) => e.target.style.color = '#6ba3ff'}
                                                        >
                                                            {fighter.name}
                                                        </a>
                                                    ) : (
                                                        fighter.name
                                                    )}
                                                </td>
                                                <td style={{ padding: '0.3em', color: '#ddd' }}>{fighter.division}</td>
                                                <td style={{ padding: '0.3em', textAlign: 'center', color: '#ddd' }}>{fighter.global_rank}</td>
                                                <td style={{ padding: '0.3em', textAlign: 'center', color: '#ddd' }}>{fighter.division_rank}</td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Highest Ranked Fighters Table */}
                    {fighters.length > 0 ? (
                        <>
                            <h2 style={{ fontSize: '1.5em', fontWeight: 500, marginBottom: '1em', color: '#ddd' }}>Highest Ranked Fighters (All Time)</h2>
                            <div style={{ border: '1px solid #555', borderRadius: '4px', overflow: 'hidden', maxHeight: '500px', overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: darkMode ? '#222' : '#f5f5f5', color: darkMode ? '#f1f1f1' : '#222' }}>
                                <th style={{ padding: '0.3em', textAlign: 'center', fontWeight: 600, borderBottom: '1px solid #555' }}>#</th>
                                <th style={{ padding: '0.3em', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #555' }}>Fighter Name</th>
                                <th style={{ padding: '0.3em', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #555' }}>Division</th>
                                <th style={{ padding: '0.3em', textAlign: 'center', fontWeight: 600, borderBottom: '1px solid #555' }}>Highest Peak</th>
                                <th style={{ padding: '0.3em', textAlign: 'center', fontWeight: 600, borderBottom: '1px solid #555' }}>
                                    <button
                                        onClick={() => handleSort('global_rank')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: darkMode ? '#f1f1f1' : '#222',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            padding: 0,
                                            fontSize: '1em',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                        }}
                                        aria-label="Sort by global rank"
                                    >
                                        Global Rank
                                        {getSortIcon('global_rank')}
                                    </button>
                                </th>
                                <th style={{ padding: '0.3em', textAlign: 'center', fontWeight: 600, borderBottom: '1px solid #555' }}>
                                    <button
                                        onClick={() => handleSort('division_rank')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: darkMode ? '#f1f1f1' : '#222',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            padding: 0,
                                            fontSize: '1em',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                        }}
                                        aria-label="Sort by division rank"
                                    >
                                        Division Rank
                                        {getSortIcon('division_rank')}
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedFighters().map((fighter, idx) => {
                                const sortedFighters = getSortedFighters();
                                const isTie = idx > 0 && fighter.global_rank === sortedFighters[idx - 1].global_rank;
                                return (
                                <tr
                                    key={fighter.name + fighter.division + idx}
                                    style={{
                                        borderBottom: '1px solid #444',
                                        background: idx % 2 === 0 ? (darkMode ? '#1a1a1a' : '#f9f9f9') : (darkMode ? '#1a1a1a' : '#f5f5f5'),
                                        transition: 'background 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = darkMode ? '#252525' : '#efefef';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = idx % 2 === 0 ? (darkMode ? '#1a1a1a' : '#f9f9f9') : (darkMode ? '#1a1a1a' : '#f5f5f5');
                                    }}
                                >
                                    <td style={{ padding: '0.3em', textAlign: 'center', color: '#ddd' }}>{isTie ? ' ' : idx + 1}</td>
                                    <td style={{ padding: '0.3em', color: '#ddd' }}>
                                        {fighter.profile_link ? (
                                            <a
                                                href={`https://www.fightmatrix.com${fighter.profile_link}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    color: '#6ba3ff',
                                                    textDecoration: 'none',
                                                    transition: 'color 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => e.target.style.color = '#8bb5ff'}
                                                onMouseLeave={(e) => e.target.style.color = '#6ba3ff'}
                                            >
                                                {fighter.name}
                                            </a>
                                        ) : (
                                            fighter.name
                                        )}
                                    </td>
                                    <td style={{ padding: '0.3em', color: '#ddd' }}>{fighter.division}</td>
                                    <td style={{ padding: '0.3em', textAlign: 'center', color: '#ddd' }}>{getIssueDate(fighter.issue)}</td>
                                    <td style={{ padding: '0.3em', textAlign: 'center', color: '#ddd' }}>{fighter.global_rank}</td>
                                    <td style={{ padding: '0.3em', textAlign: 'center', color: '#ddd' }}>{fighter.division_rank}</td>
                                </tr>
                                );
                            })}
                        </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        <div style={{ color: '#aaa', textAlign: 'center', padding: '2em', fontSize: '1.1em' }}>
                            No fighters found for this country.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default CountryProfile;
